import React, { useState, useContext, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../components/shared/useUser';
import {
  getCashTransactionClient,
  OpenTillRequest,
  CashZoneDto,
  DenominationBreakdownDto,
} from '../../../app/services/api/cashTransactions';
import DenominationBreakdown from '../components/DenominationBreakdown';
import useLogError from '../../../hooks/useLogError';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';

interface OpenTillModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const OpenTillModal: React.FC<OpenTillModalProps> = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation('cashReconciliation');
  const { selectedStore } = useContext(UserContext);
  const { logError } = useLogError();
  const cashClient = getCashTransactionClient();

  const [loading, setLoading] = useState(false);
  const [zones, setZones] = useState<CashZoneDto[]>([]);
  const [selectedZoneId, setSelectedZoneId] = useState<number | ''>('');
  const [tillNumber, setTillNumber] = useState<string>('');
  const [openingBalance, setOpeningBalance] = useState<string>('');
  const [useBreakdown, setUseBreakdown] = useState<boolean>(false);
  const [breakdown, setBreakdown] = useState<DenominationBreakdownDto | undefined>(undefined);
  const [shiftId, setShiftId] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (open && selectedStore?.storeNumber) {
      loadZones();
    }
  }, [open, selectedStore]);

  const loadZones = async () => {
    if (!selectedStore?.storeNumber) return;

    try {
      setLoading(true);
      const zonesData = await cashClient.getCashZones(selectedStore.storeNumber);
      const tillZones = zonesData.filter(z => z.zoneType === 'Till' && z.isActive);
      setZones(tillZones);
    } catch (err) {
      logError(err);
      setError(t('errors.loadZonesFailed', 'Failed to load till zones'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError('');

    // Validation
    if (!selectedZoneId) {
      setError(t('errors.selectZone', 'Please select a till zone'));
      return;
    }

    if (!tillNumber.trim()) {
      setError(t('errors.enterTillNumber', 'Please enter a till number'));
      return;
    }

    const balanceAmount = useBreakdown
      ? breakdown?.grandTotal || 0
      : parseFloat(openingBalance);

    if (isNaN(balanceAmount) || balanceAmount < 0) {
      setError(t('errors.invalidOpeningBalance', 'Please enter a valid opening balance'));
      return;
    }

    try {
      setLoading(true);

      const request: OpenTillRequest = {
        storeNumber: selectedStore?.storeNumber || '',
        tillNumber: tillNumber.trim(),
        zoneId: selectedZoneId as number,
        openingBalance: balanceAmount,
        shiftId: shiftId.trim() || undefined,
        denominationBreakdown: useBreakdown ? breakdown : undefined,
        notes: notes.trim() || undefined,
      };

      await cashClient.openTill(request);

      setShowSuccess(true);
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 1500);

    } catch (err) {
      logError(err);
      setError(t('errors.openTillFailed', 'Failed to open till. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedZoneId('');
    setTillNumber('');
    setOpeningBalance('');
    setBreakdown(undefined);
    setShiftId('');
    setNotes('');
    setError('');
    setUseBreakdown(false);
    onClose();
  };

  const selectedZone = zones.find(z => z.id === selectedZoneId);

  return (
    <>
      <Dialog open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth>
        <DialogTitle>{t('openTill.title', 'Open Till')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3}
            sx={{ mt: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              select
              label={t('openTill.tillZone', 'Till Zone')}
              value={selectedZoneId}
              onChange={(e) => setSelectedZoneId(Number(e.target.value))}
              disabled={loading}
              required
              fullWidth
            >
              {zones.map((zone) => (
                <MenuItem key={zone.id}
                  value={zone.id}>
                  {zone.zoneName}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label={t('openTill.tillNumber', 'Till Number')}
              value={tillNumber}
              onChange={(e) => setTillNumber(e.target.value)}
              disabled={loading}
              required
              fullWidth
              placeholder={t('openTill.tillNumberPlaceholder', 'e.g., Till 1')}
            />

            <TextField
              label={t('openTill.shiftId', 'Shift ID (Optional)')}
              value={shiftId}
              onChange={(e) => setShiftId(e.target.value)}
              disabled={loading}
              fullWidth
              placeholder={t('openTill.shiftIdPlaceholder', 'e.g., MORNING-001')}
            />

            {!useBreakdown ? (
              <Box>
                <TextField
                  label={t('openTill.openingBalance', 'Opening Balance (£)')}
                  type="number"
                  value={openingBalance}
                  onChange={(e) => setOpeningBalance(e.target.value)}
                  disabled={loading}
                  required
                  fullWidth
                  inputProps={{ step: '0.01', min: '0' }}
                />
                <Button
                  onClick={() => setUseBreakdown(true)}
                  sx={{ mt: 1 }}
                  size="md"
                >
                  {t('openTill.useBreakdown', 'Enter denomination breakdown')}
                </Button>
              </Box>
            ) : (
              <Box>
                <DenominationBreakdown
                  value={breakdown}
                  onChange={setBreakdown}
                  currencyCode="GBP"
                  showQuickTotalToggle={true}
                  disabled={loading}
                />
                <Button
                  onClick={() => {
                    setUseBreakdown(false);
                    setOpeningBalance(breakdown?.grandTotal?.toFixed(2) || '');
                  }}
                  sx={{ mt: 2 }}
                  size="md"
                >
                  {t('openTill.useQuickTotal', 'Use quick total instead')}
                </Button>
              </Box>
            )}

            <TextField
              label={t('openTill.notes', 'Notes (Optional)')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
              fullWidth
              multiline
              rows={2}
              placeholder={t('openTill.notesPlaceholder', 'Any additional notes...')}
            />

            {selectedZone && selectedZone.requiresTwoPersonApproval && (
              <Alert severity="info">
                {t('openTill.requiresApproval', 'This till requires two-person approval for reconciliation.')}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}
            disabled={loading}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="primary"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {t('openTill.openTill', 'Open Till')}
          </Button>
        </DialogActions>
      </Dialog>

      <MessagesSnackbar
        open={showSuccess}
        message={t('openTill.success', 'Till opened successfully')}
        severity="success"
        duration={3000}
        onClose={() => setShowSuccess(false)}
      />
    </>
  );
};

export default OpenTillModal;
